import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://www.drgrreddy.com';
const DEFAULT_IMAGE = `${BASE_URL}/favicon.png`;
const SITE_NAME = 'Dr. GR Reddy Eye Hospital';

/**
 * Reusable SEO component for managing page-level SEO metadata.
 *
 * @param {string} title - Page title
 * @param {string} description - Meta description
 * @param {string|string[]} keywords - Page keywords (string or array)
 * @param {string} canonical - Full canonical URL (e.g. 'https://www.drgrreddy.com/about')
 * @param {string} ogImage - Absolute URL of the social preview image
 * @param {string} ogType - Open Graph type ('website', 'article', etc.)
 * @param {string} twitterCard - Twitter card type ('summary_large_image', etc.)
 * @param {object|array} schemaData - Optional structured JSON-LD data object or array
 * @param {boolean} noindex - If true, sets robots to noindex, nofollow
 */
const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_IMAGE,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  schemaData = null,
  noindex = false,
}) => {
  const currentTitle = title
    ? `${title}`
    : `${SITE_NAME} | Best Eye Hospital in Tadepalligudem`;

  const currentDesc =
    description ||
    'Dr. GR Reddy Eye Hospital in Tadepalligudem provides advanced cataract surgery, vitreo-retinal care, glaucoma treatment, paediatric ophthalmology, and comprehensive vision care in West Godavari.';

  const currentKeywords = Array.isArray(keywords)
    ? keywords.join(', ')
    : keywords || '';

  const currentCanonical = canonical || BASE_URL;

  return (
    <Helmet>
      {/* Search Engine Verification */}
      <meta name="google-site-verification" content="UAMbEWnGYwgx1caO18DEhPPazb5FDjR3gGbcgwRvpzI" />
      <meta name="msvalidate.01" content="73C10A3910591BBAC1070E9F782F7B71" />

      {/* Basic HTML Meta Tags */}
      <title>{currentTitle}</title>
      <meta name="description" content={currentDesc} />
      {currentKeywords && <meta name="keywords" content={currentKeywords} />}
      <link rel="canonical" href={currentCanonical} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={currentTitle} />
      <meta property="og:description" content={currentDesc} />
      <meta property="og:url" content={currentCanonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={currentTitle} />
      <meta name="twitter:description" content={currentDesc} />
      <meta name="twitter:url" content={currentCanonical} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data (JSON-LD) */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
